import { useSyncExternalStore } from 'react';

/**
 * Hash routes: #/ home · #/announcements · #/assets · #/documents · #/search?q= · #/mine · #/dept/:id department folder ·
 * #/edit/:id editor · #/view/:id read-only viewer. Query string after `?`.
 */
export type Route =
  | { name: 'home' }
  | { name: 'announcements' }
  | { name: 'assets' }
  | { name: 'documents' }
  | { name: 'search'; q: string }
  | { name: 'mine' }
  | { name: 'dept'; id: string }
  | { name: 'edit'; id: string; query: URLSearchParams }
  | { name: 'view'; id: string };

function parse(hash: string): Route {
  const [path, qs = ''] = hash.replace(/^#/, '').split('?');
  const parts = path.split('/').filter(Boolean);
  if (parts[0] === 'edit' && parts[1]) return { name: 'edit', id: decodeURIComponent(parts[1]), query: new URLSearchParams(qs) };
  if (parts[0] === 'view' && parts[1]) return { name: 'view', id: decodeURIComponent(parts[1]) };
  if (parts[0] === 'dept' && parts[1]) return { name: 'dept', id: decodeURIComponent(parts[1]) };
  if (parts[0] === 'announcements') return { name: 'announcements' };
  if (parts[0] === 'assets') return { name: 'assets' };
  if (parts[0] === 'documents') return { name: 'documents' };
  if (parts[0] === 'search') return { name: 'search', q: new URLSearchParams(qs).get('q') ?? '' };
  if (parts[0] === 'mine') return { name: 'mine' };
  return { name: 'home' };
}

const subscribe = (cb: () => void) => {
  window.addEventListener('hashchange', cb);
  return () => window.removeEventListener('hashchange', cb);
};

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribe, () => window.location.hash);
  return parse(hash);
}

export function navigate(path: string, opts: { replace?: boolean } = {}) {
  const hash = `#${path}`;
  if (opts.replace) window.location.replace(hash);
  else window.location.hash = hash;
}

export const paths = {
  home: () => '/',
  announcements: () => '/announcements',
  assets: () => '/assets',
  documents: () => '/documents',
  search: (q: string) => `/search?q=${encodeURIComponent(q)}`,
  mine: () => '/mine',
  dept: (id: string) => `/dept/${encodeURIComponent(id)}`,
  edit: (id: string, query?: string) => `/edit/${encodeURIComponent(id)}${query ? `?${query}` : ''}`,
  view: (id: string) => `/view/${encodeURIComponent(id)}`,
};
